import { useRequeueSiteGeocoding } from '@/routes/map/SitesMapPage.hooks'
import { Button } from 'devextreme-react/button'
import { confirm } from 'devextreme/ui/dialog'
import notify from 'devextreme/ui/notify'
import { isAxiosError } from 'axios'
import { useCallback, useRef, useState } from 'react'

type Props = {
  siteId: number
  canRequeue: boolean
  hasCoordinates: boolean
  hasUnsavedChanges?: boolean
  iconOnly?: boolean
  onQueued?: (siteId: number) => void
}

export default function RequeueSiteGeocodingButton({
  siteId,
  canRequeue,
  hasCoordinates,
  hasUnsavedChanges = false,
  iconOnly = false,
  onQueued,
}: Props) {
  const { mutateAsync, isPending } = useRequeueSiteGeocoding()
  const [confirming, setConfirming] = useState(false)
  const [queued, setQueued] = useState(false)
  // Recheck after the confirmation dialog: another card may have been edited meanwhile.
  const blocked = useRef(false)
  blocked.current = !canRequeue || hasUnsavedChanges || queued

  const requeue = useCallback(async () => {
    if (blocked.current) return
    setConfirming(true)
    try {
      if (
        hasCoordinates &&
        !(await confirm(
          'Le coordinate attuali, anche se manuali, saranno rimosse. La sede verrà geocodificata alla prossima esecuzione del job. Continuare?',
          'Riaccoda geocodifica',
        ))
      )
        return
      if (blocked.current) return
      await mutateAsync(siteId)
      setQueued(true)
      onQueued?.(siteId)
      notify('Sede in coda per la prossima esecuzione del job.', 'success', 3000)
    } catch (error) {
      notify(
        isAxiosError(error) && (error.response?.status === 409 || error.response?.status === 404)
          ? 'La sede non può più essere riaccodata. Aggiorna la pagina per verificarne lo stato.'
          : 'Impossibile riaccodare la sede. Riprova.',
        'error',
        4000,
      )
    } finally {
      setConfirming(false)
    }
  }, [hasCoordinates, mutateAsync, onQueued, siteId])

  const label = isPending ? 'Accodamento...' : 'Riaccoda geocodifica'
  const elementAttr = iconOnly ? { 'aria-label': label } : undefined
  let hint = iconOnly ? label : undefined
  if (hasUnsavedChanges) hint = 'Salva prima le modifiche alla configurazione delle sedi.'

  return (
    <Button
      text={iconOnly ? undefined : label}
      elementAttr={elementAttr}
      icon="refresh"
      disabled={!canRequeue || hasUnsavedChanges || isPending || confirming || queued}
      hint={hint}
      useSubmitBehavior={false}
      onClick={requeue}
    />
  )
}
